/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

// NOTE: Using global axios instance to inherit the authentication interceptor
// which adds Authorization, X-ID-Token, and X-Refresh-Token headers for mobile browser support.
// Mobile Safari blocks third-party cookies, so auth must be sent via headers.

// ==================== Types ====================

export interface PaginationParams {
	page?: number;
	limit?: number;
}

export interface ContributionsByDateParams extends PaginationParams {
	date: string; // YYYY-MM-DD format
	timezone?: string;
}

export interface ContributionsByUserParams extends PaginationParams {
	userId: string;
}

export interface ContributionsByCircleParams extends PaginationParams {
	circleId: string;
}

export interface ContributionsByCycleParams extends PaginationParams {
	cycleId: string;
}

// ==================== Contribution Reports Service Methods ====================

/**
 * Get all contributions with pagination (admin only)
 */
export const fetchAllContributionsAsAdmin = async (
	params?: PaginationParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/contributions${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get all contributions error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get contributions for a specific date (admin only)
 */
export const fetchContributionsByDateAsAdmin = async (
	params: ContributionsByDateParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.timezone) queryParams.append("timezone", params.timezone);

		const url = `${API_BASE_URL}/admin/contributions/date/${params.date}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get contributions by date error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get contributions for a specific user (admin only)
 */
export const fetchContributionsByUserAsAdmin = async (
	params: ContributionsByUserParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/contributions/user/${params.userId}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get contributions by user error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get contributions for a specific circle (admin only)
 */
export const fetchContributionsByCircleAsAdmin = async (
	params: ContributionsByCircleParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/contributions/circle/${params.circleId}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get contributions by circle error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get contributions for a specific cycle (admin only)
 */
export const fetchContributionsByCycleAsAdmin = async (
	params: ContributionsByCycleParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/contributions/cycle/${params.cycleId}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get contributions by cycle error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get all failed contributions (admin only)
 */
export const fetchFailedContributionsAsAdmin = async (
	params?: PaginationParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/contributions/failed${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get failed contributions error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Cycle-Based Contribution Tracking Service Methods ====================

/**
 * Get all active contribution cycles (admin only)
 */
export const fetchAllActiveCyclesAsAdmin = async (
	params?: PaginationParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/cycles/active${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get active cycles error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get detailed member contribution status for a specific cycle (admin only)
 */
export const fetchCycleContributionDetailsAsAdmin = async (cycleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/cycles/${cycleId}/contributions`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get cycle contribution details error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get session-by-session breakdown of contributions within a cycle (admin only)
 */
export const fetchCycleSessionsBreakdownAsAdmin = async (cycleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/cycles/${cycleId}/sessions`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get cycle sessions breakdown error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Auto-Pay Status Service Methods ====================

/**
 * Get all users with their auto-pay status (admin only)
 */
export const fetchAllUsersAutoPayStatusAsAdmin = async (
	params?: PaginationParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/auto-pay/users${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get users auto-pay status error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-pay status for a specific user (admin only)
 */
export const fetchUserAutoPayStatusAsAdmin = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-pay/users/${userId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get user auto-pay status error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get all circles with their auto-pay summary (admin only)
 */
export const fetchAllCirclesAutoPayStatusAsAdmin = async (
	params?: PaginationParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `${API_BASE_URL}/admin/auto-pay/circles${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(url, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get circles auto-pay status error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-pay status for a specific circle (admin only)
 */
export const fetchCircleAutoPayStatusAsAdmin = async (circleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-pay/circles/${circleId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get circle auto-pay status error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};
